import { Suspense, lazy } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { Toaster } from "react-hot-toast";

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import DashboardLayout from "./components/layout/DashboardLayout";
import ApiStatusBanner from "./components/common/ApiStatusBanner";
import "./App.css";

// Configure Global TanStack Query Client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

const Login = lazy(() => import("./pages/Login"));
const Register = lazy(() => import("./pages/Register"));
const VerifyOTP = lazy(() => import("./pages/VerifyOTP"));
const SetPassword = lazy(() => import("./pages/SetPassword"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const EmployeeAssetDashboard = lazy(() => import("./pages/EmployeeAssetDashboard"));
const AssetRequests = lazy(() => import("./pages/AssetRequests"));
const AssetAllocations = lazy(() => import("./pages/AssetAllocations"));
const AssetInventory = lazy(() => import("./pages/AssetInventory"));
const InventoryReport = lazy(() => import("./pages/InventoryReport"));
const MyAssetRequests = lazy(() => import("./pages/MyAssetRequests"));
const AcceptAssetPage = lazy(() => import("./pages/AcceptAssetPage"));
const HolidayList = lazy(() => import("./pages/HolidayList"));
const MyLeaves = lazy(() => import("./pages/MyLeaves"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const ProfilePage = lazy(() => import("./pages/ProfilePage"));
const HRDashboard = lazy(() => import("./pages/HRDashboard"));
const HREmployeeList = lazy(() => import("./pages/HREmployeeList"));
const HRHolidayList = lazy(() => import("./pages/HRHolidayList"));
const HREmployeeBirthdays = lazy(() => import("./pages/HREmployeeBirthdays"));
const LeaveReport = lazy(() => import("./pages/LeaveReport"));
const LeaveBalanceReport = lazy(() => import("./pages/LeaveBalanceReport"));
const HRAnnouncementList = lazy(() => import("./pages/HRAnnouncementList"));
const PendingLeaves = lazy(() => import("./pages/PendingLeaves"));
const LeaveDetailsPage = lazy(() => import("./pages/LeaveDetailsPage"));
const CandidatesTracker = lazy(() => import("./pages/CandidatesTracker"));
const CandidateReport = lazy(() => import("./pages/CandidateReport"));
const Settings = lazy(() => import("./pages/Settings"));
const Announcements = lazy(() => import("./pages/Announcements"));
const DirectEmail = lazy(() => import("./pages/DirectEmail"));
const HRPolicies = lazy(() => import("./pages/HRPolicies"));
const BugReport = lazy(() => import("./pages/BugReport"));
const BugReportsList = lazy(() => import("./pages/BugReportsList"));
const EmailLogs = lazy(() => import("./pages/EmailLogs"));

const ConnectivityTest = lazy(() => import("./components/ConnectivityTest"));

const RouteFallback = () => (
  <div className="d-flex justify-content-center align-items-center min-vh-100">
    Loading...
  </div>
);

const renderLazyRoute = (Component) => (
  <Suspense fallback={<RouteFallback />}>
    <Component />
  </Suspense>
);

const getStoredUser = () => {
  try {
    return JSON.parse(localStorage.getItem("user") || "{}");
  } catch {
    return {};
  }
};

const renderSettingsRoute = () => {
  const user = getStoredUser();
  const role = (user?.role || "").toLowerCase().replace(/\s+/g, "_");
  const isAdmin =
    role === "admin" ||
    role === "administrator" ||
    role === "super_admin" ||
    user?.is_staff === true ||
    user?.is_staff === "true" ||
    user?.is_superuser === true ||
    user?.is_superuser === "true";

  return isAdmin ? renderLazyRoute(Settings) : <Navigate to="/dashboard" replace />;
};

const renderEmailLogsRoute = () => {
  const user = getStoredUser();
  const role = (user?.role || "").toLowerCase().replace(/\s+/g, "_");
  const isAdmin =
    role === "admin" ||
    role === "administrator" ||
    role === "super_admin" ||
    user?.is_staff === true ||
    user?.is_staff === "true" ||
    user?.is_superuser === true ||
    user?.is_superuser === "true";

  return isAdmin ? renderLazyRoute(EmailLogs) : <Navigate to="/dashboard" replace />;
};

const hasRecruitmentAccess = (user) => {
  const role = (user?.role || "").toLowerCase().replace(/\s+/g, "_");
  return (
    role === "hr" ||
    role === "admin" ||
    role === "administrator" ||
    role === "super_admin" ||
    user?.is_superuser === true ||
    user?.is_superuser === "true"
  );
};

const renderCandidatesRoute = () => {
  return hasRecruitmentAccess(getStoredUser())
    ? renderLazyRoute(CandidatesTracker)
    : <Navigate to="/dashboard" replace />;
};

const renderCandidateReportRoute = () => {
  return hasRecruitmentAccess(getStoredUser())
    ? renderLazyRoute(CandidateReport)
    : <Navigate to="/dashboard" replace />;
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <ApiStatusBanner />
        <Toaster position="top-right" />
        <Routes>
          <Route path="/login" element={renderLazyRoute(Login)} />
        <Route path="/register" element={renderLazyRoute(Register)} />
        <Route path="/verify-otp" element={renderLazyRoute(VerifyOTP)} />
        <Route path="/set-password" element={renderLazyRoute(SetPassword)} />
        <Route path="/forgot-password" element={renderLazyRoute(ForgotPassword)} />
        <Route path="/reset-password" element={renderLazyRoute(ResetPassword)} />

        <Route
          path="/accept-asset/:token"
          element={renderLazyRoute(AcceptAssetPage)}
        />

        <Route element={<DashboardLayout />}>
          <Route path="/dashboard" element={renderLazyRoute(Dashboard)} />
          <Route path="/profile" element={renderLazyRoute(ProfilePage)} />
          <Route path="/profile/:id" element={renderLazyRoute(ProfilePage)} />
          <Route path="/assets" element={renderLazyRoute(EmployeeAssetDashboard)} />
          <Route
            path="/my-assets"
            element={renderLazyRoute(EmployeeAssetDashboard)}
          />
          <Route path="/holidays" element={renderLazyRoute(HolidayList)} />
          <Route path="/my-leaves" element={renderLazyRoute(MyLeaves)} />
          <Route path="/announcements" element={renderLazyRoute(Announcements)} />
          <Route path="/hr-policies" element={renderLazyRoute(HRPolicies)} />
          <Route path="/bug-report" element={renderLazyRoute(BugReport)} />
          <Route path="/leave-approvals" element={renderLazyRoute(PendingLeaves)} />
          <Route path="/leave-requests/:id" element={renderLazyRoute(LeaveDetailsPage)} />
          <Route path="/reports/leaves" element={renderLazyRoute(LeaveReport)} />
          <Route path="/reports/leave-balances" element={renderLazyRoute(LeaveBalanceReport)} />
          <Route path="/reports/leave-balance" element={renderLazyRoute(LeaveBalanceReport)} />
          <Route path="/reports/bugs" element={renderLazyRoute(BugReportsList)} />

          <Route path="/settings" element={renderSettingsRoute()} />
          <Route path="/admin/email-logs" element={renderEmailLogsRoute()} />

          <Route path="/asset-requests" element={renderLazyRoute(AssetRequests)} />
          <Route
            path="/asset-allocations"
            element={renderLazyRoute(AssetAllocations)}
          />
          <Route path="/assets/items" element={renderLazyRoute(AssetInventory)} />
          <Route
            path="/reports/inventory"
            element={renderLazyRoute(InventoryReport)}
          />
          <Route
            path="/asset-requests/my-requests"
            element={renderLazyRoute(MyAssetRequests)}
          />

          <Route path="/hr/dashboard" element={renderLazyRoute(HRDashboard)} />
          <Route
            path="/hr/employees"
            element={renderLazyRoute(HREmployeeList)}
          />
          <Route
            path="/hr/holidays"
            element={renderLazyRoute(HRHolidayList)}
          />
          <Route
            path="/hr/birthdays"
            element={renderLazyRoute(HREmployeeBirthdays)}
          />
          <Route
            path="/birthdays"
            element={renderLazyRoute(HREmployeeBirthdays)}
          />
          <Route
            path="/hr/announcements"
            element={renderLazyRoute(HRAnnouncementList)}
          />
          <Route
            path="/recruitment/candidates"
            element={renderCandidatesRoute()}
          />
          <Route
            path="/reports/candidates"
            element={renderCandidateReportRoute()}
          />
          <Route
            path="/send-email"
            element={
              hasRecruitmentAccess(getStoredUser()) ? (
                renderLazyRoute(DirectEmail)
              ) : (
                <Navigate to="/dashboard" replace />
              )
            }
          />
        </Route>

        <Route path="/test" element={renderLazyRoute(ConnectivityTest)} />
        <Route
          path="/asset-dashboard"
          element={<Navigate to="/assets" replace />}
        />
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
      </Routes>
      <ReactQueryDevtools initialIsOpen={false} position="bottom" />
    </Router>
  </QueryClientProvider>
  );
}

export default App;
